import dayjs from "dayjs";
import {
  buildEmployeeProfilePayload,
  buildPayrollPayload,
  buildAddressPayload,
  buildEmploymentPayload,
} from "../../../Payloads/EmployeeSavePayload";

/**
 * Sanitizes an employee name segment by removing special characters.
 * @param {string} name
 * @returns {string}
 */
export const cleanNamePart = (name) => {
  return String(name || "")
    .replace(/[^a-zA-Z0-9\s]/g, "")
    .trim();
};

/**
 * Cleans and trims phone numbers to at most 10 numeric digits.
 * @param {string|number} val
 * @returns {string}
 */
export const cleanPhone = (val) => {
  if (val == null) return "";
  return String(val).replace(/[^0-9]/g, "").slice(0, 10);
};

/**
 * Splits a full employee name into First, Middle, and Last name components.
 * Handles various name permutations (single word, 2-part, 3-part 'FN LN MN').
 * @param {string} fullName
 * @returns {{ firstName: string, middleName: string, lastName: string }}
 */
export const splitEmployeeName = (fullName) => {
  if (!fullName) {
    return { firstName: "", middleName: "", lastName: "" };
  }

  const sanitized = String(fullName)
    .replace(/[^a-zA-Z0-9\s]/g, " ")
    .trim();

  const parts = sanitized
    .split(/\s+/)
    .map(cleanNamePart)
    .filter(Boolean);

  if (parts.length === 0) {
    return { firstName: "", middleName: "", lastName: "" };
  }
  if (parts.length === 1) {
    return { firstName: parts[0], middleName: "", lastName: "" };
  }
  if (parts.length === 2) {
    return { firstName: parts[0], middleName: "", lastName: parts[1] };
  }

  // Handle standard 3-part name case: First, Last, Middle
  if (parts.length === 3) {
    return {
      firstName: parts[0],
      lastName: parts[1],
      middleName: parts[2],
    };
  }

  return {
    firstName: parts[0] || "",
    lastName: parts[1] || "",
    middleName: parts.slice(2).join(" "),
  };
};

/**
 * Validates required fields and business constraints for employee submission.
 * @param {Object} values - Current Formik values.
 * @returns {string|null} - Error message string if invalid, or null if valid.
 */
export const validateEmployeeForm = (values) => {
  if (!values.corporation) return "Please fill Corporation";
  if (!values.firstName || !values.firstName.trim()) return "Please fill First Name";
  if (values.firstName && values.firstName.length > 50) return "First Name cannot exceed 50 characters.";
  if (values.middleName && values.middleName.length > 50) return "Middle Name cannot exceed 50 characters.";
  if (values.lastName && values.lastName.length > 50) return "Last Name cannot exceed 50 characters.";
  if (values.emailId && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.emailId)) return "Invalid Email Format in Employee Profile.";
  if (values.emergencyContact?.emailId && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.emergencyContact.emailId)) return "Invalid email address in Emergency Contact.";
  if (!values.FrequencyID) return "Please select Pay frequency";

  const hasIncompletePayrollRow = values.payrollRows?.some((r) => !r.department || !r.jobTitle);
  if (hasIncompletePayrollRow) return "Please fill department and job title";

  const hasInvalidPayrollItem = values.payrollRows?.some(
    (r) =>
      !r.payrollItems ||
      r.payrollItems.length === 0 ||
      r.payrollItems.some((item) => !item.itemName || !item.account)
  );
  if (hasInvalidPayrollItem) {
    return "Item Name and COA cannot be empty in Payroll Details.";
  }

  return null;
};

/**
 * Assembles the full backend save/update payload for an employee.
 * @param {Object} values - Formik values.
 * @param {Object} editData - Original metadata when in edit mode.
 * @param {string|number|null} employeeId - Active employee ID if updating.
 * @param {boolean} isCloning - Whether cloning to other corporations is enabled.
 * @param {Array} cloneCorps - List of target clone corporations.
 * @returns {Object} Complete API request payload.
 */
export const generateEmployeePayload = (values, editData, employeeId, isCloning, cloneCorps) => {
  const sourceCorpId = values.corporation;
  const cloneCorpIds = Array.isArray(cloneCorps)
    ? cloneCorps
      .map((c) => c?.corpValue || c)
      .filter((id) => String(id) !== String(sourceCorpId))
    : [];

  return {
    ...buildEmployeeProfilePayload(values, editData),
    ...buildPayrollPayload(values, editData),
    ...buildAddressPayload(values, editData),
    ...buildEmploymentPayload(values, editData),

    FrequencyID: values.FrequencyID ?? "",
    PayFrequency: values.PayFrequency ?? "",
    RelationId: values.emergencyContact?.relationId || "",
    RelationName: values.emergencyContact?.relationName || "",
    Notes: values.notes || null,
    IsCloning: isCloning,
    CorpIDs: isCloning ? cloneCorpIds : [],
    IsNew: !employeeId,
    IsUpdate: !!employeeId,
    IsImport: false,
    IsSave: true,
    Type: 2,
  };
};

/**
 * Creates default Formik initial values for an employee.
 * Extracted outside component render body to prevent expensive re-instantiation on every render.
 * @param {Object} props - Component props containing optional prefill details.
 * @param {string} corpId - Current corporation ID from route params.
 * @returns {Object} Default employee Formik values.
 */
export const createDefaultEmployeeValues = (props = {}, corpId = "") => {
  const {
    employeeName,
    payFrequency,
    corporationId,
    corporationName,
    defaultDeptId,
    defaultJobId,
    profitCenterId,
  } = props;

  const nameParts = splitEmployeeName(employeeName);

  const formatChecksToBePrintedAs = (fn, mn, ln) => {
    const segments = [];
    if (fn) segments.push(fn);
    const remainingParts = [ln, mn].filter(Boolean).join(" ");
    if (remainingParts) segments.push(remainingParts);
    return segments.join(", ");
  };

  const initialCorpValue = corporationId || (corpId && corpId !== "000000000000000000000000000000000000" ? corpId : "000000000000000000000000000000000000");

  return {
    id: null,
    corporation: initialCorpValue,
    corporationName: corporationName || "",
    salutation: "",
    firstName: nameParts.firstName || "",
    middleName: nameParts.middleName || "",
    lastName: nameParts.lastName || "",
    gender: "",
    mobileNumber: "",
    emailId: "",
    date: dayjs().format("MM-DD-YYYY"),
    maritalStatus: 0,
    ssn: "",
    checksToBePrintedAs: formatChecksToBePrintedAs(nameParts.firstName, nameParts.middleName, nameParts.lastName),
    addToAllCorporations: false,
    active: true,

    PayFrequency: payFrequency?.label || "",
    FrequencyID: payFrequency?.value || "",
    payrollRows: defaultDeptId && defaultJobId ? [{
      id: 1,
      isDefault: 1,
      department: defaultDeptId,
      jobTitle: defaultJobId,
      profitCenter: profitCenterId || "",
      isUsedEmp: false,
      departmentError: "",
      jobTitleError: "",
      payrollItems: [],
    }] : [],
    deletedPayrollIds: [],

    notes: "",
    attachments: [],

    addressDetails: {
      addressName: "",
      address: "",
      city: "",
      countryId: null,
      countryName: "",
      countryCode: "",
      stateId: "",
      stateName: "",
      stateCode: "",
      zipCode: "",
    },

    emergencyContact: {
      name: "",
      relationId: "",
      relationName: "",
      mobileNum: "",
      homeNum: "",
      AlternativeNum: "",
      workNum: "",
      faxNum: "",
      emailId: "",
    },

    employmentInfo: {
      employeeDetails: {
        dob: "",
        hireDate: "",
        startDate: "",
        usCitizen: false,
        i9OnFile: false,
        i9ExpirationDate: "",
      },
      additionalInfo: {
        homeNum: "",
        AlternativeNum: "",
        workNum: "",
        faxNum: "",
        bankName: "",
        bankACNum: "",
        bankId: null,
        emailId: "",
      },
      jobDetails: {
        lcaState: "0.00",
        lcaGC: "0.00",
        jobType: 0,
      },
      terminationDetails: {
        terminationType: 0,
        terminationReason: "",
        lastDayWorked: "",
        lastDayOnBenefits: "",
        releaseDate: "",
      },
    },
  };
};

/**
 * Transforms backend API data for an employee into the structured Formik values shape.
 * Groups flat payroll details into parent rows by Department, JobTitle, and ProfitCenter.
 * @param {Object} apiData - Raw employee response from getEmployeeById.
 * @param {Object} defaultValues - Fallback default form structure.
 * @returns {Object} Mapped Formik values.
 */
export const mapApiDataToEmployeeForm = (apiData, defaultValues) => {
  const employeeContact = apiData.RelationContactReq?.[0] || {};
  const emergencyContactData = apiData.RelationContactReq?.[1] || {};
  const address = apiData.SaveAddressReq || {};
  const empDetails = apiData.EmploymentDetailsRequest || {};
  const payrollDetails = apiData.PayrollDetails || [];

  // Group Payroll Details into parent Department + Job rows
  const groupedPayroll = [];
  payrollDetails.forEach((item) => {
    const pcId = item.PCID || item.ProfitCenterID || item.ProfitCenterId || item.ProfitCenter || "";
    let existingRow = groupedPayroll.find(
      (r) =>
        r.department === item.Department &&
        r.jobTitle === item.JobTitle &&
        r.profitCenter === pcId,
    );

    if (!existingRow) {
      existingRow = {
        id: groupedPayroll.length + 1,
        isDefault: item.IsDefault === true || item.IsDefault === 1 ? 1 : 0,
        department: item.Department || "",
        jobTitle: item.JobTitle || "",
        profitCenter: pcId || "",
        isUsedEmp: Boolean(item.IsUsedEMP || item.isUsedEMP || item.IsUsedEmp || item.isUsedEmp),
        departmentError: "",
        jobTitleError: "",
        payrollItems: [],
      };
      groupedPayroll.push(existingRow);
    }

    existingRow.payrollItems.push({
      payrollDetailId: item.Id || item.ID || null,
      payrollDeptJobId: item.PayrollDeptJobId || item.PayrollItemDeptJobDetailsID || 0,
      itemName: item.ItemName || "",
      account: item.COA || "",
      payrollItemId: item.PayrollItemID || item.PayrollItemId || "",
      rate: item.Rate !== undefined && item.Rate !== null ? parseFloat(item.Rate).toFixed(2) : "0.00",
    });
  });

  return {
    ...defaultValues,
    id: apiData.EMPID || apiData.ID,
    corporation: apiData.CorporationID || "",
    corporationName: apiData.CorporationName || "",
    salutation: apiData.Salutation || "",
    firstName: apiData.FirstName || "",
    middleName: apiData.MiddleName || "",
    lastName: apiData.LastName || "",
    gender: apiData.Gender || "",
    emailId: apiData.EmailID || employeeContact.EmailID || "",
    maritalStatus: apiData.MaritalStatus || 0,
    mobileNumber: cleanPhone(apiData.MobileNumber || employeeContact.MobileNum),
    ssn: apiData.SSN || "",
    checksToBePrintedAs: apiData.CheckToBePrintedAs || "",
    active: apiData.IsActive === 1,
    PayFrequency: apiData.PayFrequency || "",
    FrequencyID: apiData.FrequencyID || "",
    payrollRows: groupedPayroll,
    notes: apiData.Notes || "",
    addressDetails: {
      addressName: address.AddressName || "",
      address: address.Adress || "",
      city: address.City || "",
      countryId: address.CountryID || null,
      stateId: address.StateID ? String(address.StateID) : "",
      stateName: address.StateName || "",
      zipCode: address.ZipCode || "",
    },
    emergencyContact: {
      name: emergencyContactData.ContactName || "",
      relationId: emergencyContactData.RelationId || apiData.RelationId || "",
      relationName: emergencyContactData.Name || apiData.RelationName || "",
      mobileNum: cleanPhone(emergencyContactData.MobileNum),
      homeNum: cleanPhone(emergencyContactData.HomeNum),
      alternativeNum: cleanPhone(emergencyContactData.AlternativeNum),
      workNum: cleanPhone(emergencyContactData.WorkNum),
      faxNum: cleanPhone(emergencyContactData.FaxNum),
      emailId: emergencyContactData.EmailID || "",
    },
    employmentInfo: {
      ...defaultValues.employmentInfo,
      employeeDetails: {
        dob: apiData.DOB || "",
        hireDate: empDetails.HireDate || "",
        startDate: empDetails.StartDate || "",
        usCitizen: Boolean(empDetails.USCitizen),
        i9OnFile: Boolean(empDetails.I9file),
        i9ExpirationDate: empDetails.I9ExpiryDate || "",
      },
      additionalInfo: {
        ...defaultValues.employmentInfo.additionalInfo,
        homeNum: cleanPhone(employeeContact.HomeNum),
        alternativeNum: cleanPhone(employeeContact.AlternativeNum),
        workNum: cleanPhone(employeeContact.WorkNum),
        faxNum: cleanPhone(employeeContact.FaxNum),
        bankName: apiData.BankName || "",
        bankACNum: apiData.BankAccountNum || "",
        bankId: apiData.BankID || null,
        emailId: apiData.EmailID || "",
      },
      jobDetails: {
        lcaState: empDetails.LCAState != null ? Number(empDetails.LCAState).toFixed(2) : "",
        lcaGC: empDetails.LCAGC != null ? Number(empDetails.LCAGC).toFixed(2) : "",
        jobType: empDetails.JobType || 0,
      },
      terminationDetails: {
        terminationType: empDetails.TerminationType ?? 0,
        terminationReason: empDetails.TerminationReason || "",
        lastDayWorked: empDetails.LastDayWorked || "",
        lastDayOnBenefits: empDetails.LastDayOnBenifits || "",
        releaseDate: empDetails.ReleaseDate || "",
      },
    },
  };
};
